/* eslint-disable react-hooks/exhaustive-deps */
import {Spin} from "antd"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import {handleError, toastError} from "helpers"
import React, {useContext, useEffect, useState} from "react"
import {studentPaymentService} from "services"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import {StudentCharge} from "types/student-account/student-ledger"
import {
    PaymentCreatedFromPlatform,
    RecurringPaymentInterval,
    StudentPaymentMethodType,
    StudentPaymentSupportedCurrency,
    StudentRecurringPayment,
    StudentRecurringPaymentPlanItem
} from "types/student-account/student-payment"
import styles from "./DirectEmbedPayment.module.css"

declare global {
    interface Window {
        aw: any
        AWAPIV4: any
        __awResponse: any
        $: any
    }
}

export enum PaymentMethod {
    CARD = "card",
    ACH = "ach"
}

export interface DirectEmbedPaymentProps {
    isOpen: boolean
    setIsOpen: Function
    method: PaymentMethod
    publishedKey: string
    providerConfig: any
    studentId: number
    paymentAmount: number
    currency: StudentPaymentSupportedCurrency
    selectedPayment?: GeneralPayment
    customNote: string
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
    isPaymentRecurring?: boolean
    // Only used when isPaymentRecurring is true: sets up a recurring subscription (via
    // createDirectEmbedRecurringSubscription) instead of a one-time payment. The tokenized card
    // captured by the modal becomes the subscription's saved payment method, and its first cycle
    // is charged immediately.
    recurringInterval?: RecurringPaymentInterval
    recurringIntervalCount?: number
    recurringCancelAt?: string | null
    recurringPaymentPlanItems?: Partial<StudentRecurringPaymentPlanItem>[]
    recurringPaymentPlanId?: number | null
    onRecurringSuccess?: (recurringPayment: StudentRecurringPayment) => void
}

// Mirrors DIRECT_EMBED_ENVIRONMENT_URLS in edular-core's DirectEmbedClient.ts — the path prefix
// differs by environment too, not just the domain (production is white-labeled under a different
// domain AND prefix). Keep both in sync if either changes.
const DIRECT_EMBED_ENVIRONMENT_CONSTANTS = {
    production: {
        scriptSrc: "https://secure.directbiller.com",
        pathPrefix: "/pbills"
    },
    sandbox: {
        scriptSrc: "https://staging.aliaswire.com",
        pathPrefix: "/bills"
    }
}
export function DirectEmbedPayment(props: DirectEmbedPaymentProps) {
    const {
        method,
        publishedKey,
        isOpen,
        setIsOpen,
        providerConfig,
        studentId,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear,
        isPaymentRecurring,
        recurringInterval,
        recurringIntervalCount,
        recurringCancelAt,
        recurringPaymentPlanItems,
        recurringPaymentPlanId,
        onRecurringSuccess
    } = props
    const [scriptLoaded, setScriptLoaded] = useState(false)
    const [awInstance, setAwInstance] = useState<any>(null)
    const [sdkReady, setSdkReady] = useState(false)
    const {setPaymentStatus, setPaymentError} = useContext(StudentPaymentContext)
    const [loading, setLoading] = useState(false)
    const [studentPayment, setStudentPayment] = useState<any | undefined>()
    const studentPaymentRef = React.useRef<any>(null)
    useEffect(() => {
        studentPaymentRef.current = studentPayment
    }, [studentPayment, studentPaymentRef])

    // Utility for loading external scripts
    const loadScript = (src: string) =>
        new Promise<void>((resolve, reject) => {
            const exists = document.querySelector(`script[src="${src}"]`)
            if (exists) return resolve()

            const script = document.createElement("script")
            script.src = src
            script.async = true
            script.onload = () => resolve()
            script.onerror = reject
            document.body.appendChild(script)
        })

    const loadCss = (href: string) =>
        new Promise<void>((resolve) => {
            const exists = document.querySelector(`link[href="${href}"]`)
            if (exists) return resolve()

            const link = document.createElement("link")
            link.rel = "stylesheet"
            link.href = href
            link.onload = () => resolve()
            document.head.appendChild(link)
        })

    const handleSubmit = async (data: any) => {
        try {
            const token = data.responseJsonObj?.tokenNumber
            const tokenizedPayerFullName = data.responseJsonObj?.accountHolder
            const studentPayment = data?.studentPayment
            const isPaymentRecurring = data?.isPaymentRecurring
            const cvv = data?.cvv

            setPaymentStatus("processing")
            setPaymentError(undefined)
            setLoading(true)

            if (isPaymentRecurring) {
                // Setting up a subscription: the tokenized card/bank account becomes the
                // subscription's saved payment method and its first cycle is charged immediately —
                // no pre-created one-time StudentPayment is involved (see the skipped effect below).
                const recurringPayment = await studentPaymentService.createDirectEmbedRecurringSubscription({
                    profileId: studentId,
                    amountInCents: Math.round(paymentAmount * 100),
                    currency,
                    interval: recurringInterval ?? RecurringPaymentInterval.Month,
                    intervalCount: recurringIntervalCount,
                    cancelAt: recurringCancelAt ?? null,
                    tokenizedCard: token,
                    payerFullName: payerFullName ?? tokenizedPayerFullName,
                    methodType:
                        method === PaymentMethod.ACH ? StudentPaymentMethodType.Ach : StudentPaymentMethodType.Card,
                    cvv,
                    tokenResponse: data.responseJsonObj,
                    paymentPlanItems: recurringPaymentPlanItems,
                    paymentPlanId: recurringPaymentPlanId
                })
                setPaymentStatus("confirmed")
                onRecurringSuccess?.(recurringPayment)
                return
            }

            await studentPaymentService.makeDirectEmbedStudentPayment({
                studentProfileId: studentPayment.studentProfileId,
                paymentId: studentPayment.paymentId,
                tokenizedCard: token,
                payerFullName: tokenizedPayerFullName,
                cvv,
                isPaymentRecurring,
                tokenResponse: data.responseJsonObj
            })

            setPaymentStatus("confirmed")
        } catch (error: any) {
            setPaymentStatus("failed")
            setPaymentError(error.message ?? "Payment failed")
        } finally {
            setLoading(false)
        }
    }

    // Open modal
    const openModal = () => {
        if (!sdkReady) return alert("SDK not loaded yet")
        if (!awInstance) return alert("AW instance not ready")

        awInstance.removeModal()
        awInstance.createModal()
        setTimeout(() => {
            watchModalClose()
            awInstance.toggleModal()
        }, 50)

        if (method === "card") {
            awInstance.ccEnabled(true)
            awInstance.achEnabled(false)
        } else if (method === "ach") {
            awInstance.ccEnabled(false)
            awInstance.achEnabled(true)
        }

        const input = document.createElement("input")
        input.type = "hidden"
        input.value = publishedKey
        document.body.appendChild(input)

        awInstance.setPublishedKey(input)
    }

    const watchModalClose = () => {
        const modal = document.getElementById("paymentModal")
        if (!modal) return

        const observer = new MutationObserver(() => {
            const isHidden =
                modal.style.display === "none" ||
                (modal.classList.contains("fade") && !modal.classList.contains("show"))

            if (isHidden) {
                setIsOpen(false)
                observer.disconnect()
            }
        })

        observer.observe(modal, {
            attributes: true,
            attributeFilter: ["style", "class"]
        })
    }

    // Load all dependencies exactly like original index.html
    useEffect(() => {
        if (!providerConfig || !isOpen) return

        setScriptLoaded(false)
        ;(async () => {
            try {
                await loadCss("https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css")
                await loadCss("https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css")

                await loadScript("https://code.jquery.com/jquery-3.4.1.js")
                await loadScript("https://cdnjs.cloudflare.com/ajax/libs/popper.js/1.14.7/umd/popper.min.js")
                await loadScript("https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/js/bootstrap.min.js")
                const {scriptSrc, pathPrefix} = DIRECT_EMBED_ENVIRONMENT_CONSTANTS[providerConfig.paymentEnvironment]
                await loadScript(`${scriptSrc}${pathPrefix}/script/awapi/transactionservice/v4.do`)

                const inst = new window.AWAPIV4.TS()
                window.aw = inst
                setAwInstance(inst)

                window.__awResponse = function (v3RespObj, responseJsonObj, resultCode, detailReason) {
                    setIsOpen(false)

                    if (resultCode === "A000") {
                        window.aw.toggleModal()
                        const token1 = v3RespObj?.getToken?.()
                        const cvv = token1?.getCvv?.()
                        handleSubmit({
                            v3RespObj,
                            responseJsonObj,
                            resultCode,
                            detailReason,
                            studentPayment: studentPaymentRef.current,
                            cvv,
                            isPaymentRecurring
                        })
                    } else {
                        throw new Error(detailReason || "Failed to tokenize card")
                    }
                }

                setSdkReady(true)
                setScriptLoaded(true)
            } catch (err) {
                console.error("ERROR LOADING SDK:", err)
            }
        })()
    }, [providerConfig, isOpen])

    useEffect(() => {
        // Recurring setup doesn't pre-create a one-time StudentPayment — the recurring endpoint
        // creates its own first-cycle payment once the modal returns a token (see handleSubmit).
        if (!studentId || !paymentAmount || !isOpen || isPaymentRecurring) {
            return
        }
        ;(async () => {
            try {
                const {studentPayment} = await studentPaymentService.createDirectEmbedStudentPayment({
                    amountInCents: Math.round(paymentAmount * 100),
                    currency,
                    studentProfileId: studentId,
                    generalPaymentId: selectedPayment?.generalPaymentId,
                    payerFullName,
                    academicYear,
                    // One-time payment only (this effect is skipped when isPaymentRecurring) —
                    // link every selected item at its own remaining amount, not the aggregate total.
                    paymentPlanItems: selectedPaymentItems?.length
                        ? selectedPaymentItems
                              .filter((item) => item.paymentAmount)
                              .map((item) => ({
                                  paymentPlanId: item.paymentPlanId,
                                  paymentPlanItemId: item.paymentPlanItemId,
                                  amount: item.paymentAmount
                              }))
                        : undefined,
                    notes: `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`,
                    createdFromPlatform: PaymentCreatedFromPlatform.EdularWeb
                })
                if (selectedCharges?.length) {
                    await studentPaymentService.assignPaymentCharges(
                        studentPayment.paymentId,
                        selectedCharges.map((charge) => ({chargeId: charge.chargeId, amount: charge.paymentAmount}))
                    )
                }
                setStudentPayment(studentPayment)
            } catch (error) {
                handleError(error)
            }
        })()
    }, [
        studentId,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear,
        isOpen,
        isPaymentRecurring
    ])

    useEffect(() => {
        if (isOpen && scriptLoaded && sdkReady) {
            openModal()
        }
    }, [isOpen, scriptLoaded, sdkReady])

    return (
        <>
            {!scriptLoaded && isOpen && (
                <div className={styles.loaderOverlay}>
                    <Spin />
                </div>
            )}
        </>
    )
}
